// ============================================================================
// First-party feature usage (docs/areas/usage.md). Which screens are opened and
// which key actions used, as counters on our own server: never an id, never
// content, never wellbeing. This file is the pure half: what may be recorded,
// the tracker that batches it, the catalogue of features and the ranking the
// Usage page draws. `state/usage.ts` wires it to the api.
// ============================================================================

/** A feature is `screen:<route pattern>` or `action:<name>`. */
export type Feature = `screen:${string}` | `action:${string}`

/**
 * Words that keep a feature out of the counters. The wellbeing module is
 * private (docs/areas/wellbeing.md); the server holds the same list
 * (`usageDenied`) and drops these too.
 */
const DENIED = ['wellbeing', '/reset', '/checkin', 'check-in', 'smok', 'anchor', 'alarm', 'mood', 'anxiety']

const SHAPE = /^(screen|action):[A-Za-z0-9_$/.:-]{1,64}$/

export const usageAllowed = (feature: string): boolean => {
  if (!SHAPE.test(feature)) return false
  const f = feature.toLowerCase()
  return !DENIED.some((d) => f.includes(d))
}

export type Hit = { feature: string; n: number }

/**
 * Counts uses in memory and hands them over in batches. A failed send is
 * dropped, not retried: usage is never worth a queue, and it must never touch
 * the user's own writes.
 */
export class UsageTracker {
  private counts = new Map<string, number>()
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(
    private send: (hits: Hit[]) => Promise<unknown>,
    private enabled: () => boolean,
    private delayMs = 15_000,
  ) {}

  track(feature: string) {
    if (!this.enabled() || !usageAllowed(feature)) return
    this.counts.set(feature, (this.counts.get(feature) ?? 0) + 1)
    if (!this.timer) this.timer = setTimeout(() => void this.flush(), this.delayMs)
  }

  async flush() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
    if (!this.counts.size) return
    const hits = [...this.counts].map(([feature, n]) => ({ feature, n }))
    this.counts.clear()
    try {
      await this.send(hits)
    } catch {
      /* dropped on purpose */
    }
  }
}

export type UsageRow = {
  feature: string
  d7: number
  p7: number
  d30: number
  p30: number
  d90: number
  p90: number
  phone: number
  desktop: number
  daily: number[]
  last_day: string
}

export type FeatureUsageReport = {
  enabled: boolean
  since: string
  today: string
  features: UsageRow[]
}

export type UsageWindow = 7 | 30 | 90

const ACTION_WORD: Record<string, string> = {
  pin: 'Pin',
  remind: 'Remind',
  pause: 'Pause',
  resume: 'Resume',
  complete: 'Complete',
  reopen: 'Reopen',
  reschedule: 'Reschedule',
  people: 'People',
  share: 'Share',
  'share-with': 'Share with',
  'nudge-alfie': 'Nudge',
  'nudge-members': 'Nudge members',
  'add-date': 'Add a date',
  edit: 'Edit',
  delete: 'Delete',
}

/** Every feature the app has, with the words the page uses for it. */
export const USAGE_CATALOGUE: { feature: string; label: string }[] = [
  { feature: 'screen:/', label: 'Today' },
  { feature: 'screen:/home', label: 'Home' },
  { feature: 'screen:/inbox', label: 'Inbox' },
  { feature: 'screen:/upcoming', label: 'Upcoming' },
  { feature: 'screen:/someday', label: 'Someday' },
  { feature: 'screen:/calendar', label: 'Calendar' },
  { feature: 'screen:/review', label: 'Review' },
  { feature: 'screen:/recurring', label: 'Recurring' },
  { feature: 'screen:/projects', label: 'Projects' },
  { feature: 'screen:/projects/$id', label: 'A project' },
  { feature: 'screen:/tasks/$id', label: 'A task' },
  { feature: 'screen:/pages', label: 'Pages' },
  { feature: 'screen:/pages/$id', label: 'A page' },
  { feature: 'screen:/lists', label: 'Lists' },
  { feature: 'screen:/lists/$id', label: 'A list' },
  { feature: 'screen:/dates', label: 'Dates' },
  { feature: 'screen:/people', label: 'People' },
  { feature: 'screen:/day', label: 'Day' },
  { feature: 'screen:/log', label: 'Log' },
  { feature: 'screen:/captures', label: 'Captures' },
  { feature: 'screen:/trash', label: 'Trash' },
  { feature: 'screen:/search', label: 'Search' },
  { feature: 'screen:/plan', label: 'Plan' },
  { feature: 'screen:/morning', label: 'Morning' },
  { feature: 'screen:/recap', label: 'Recap' },
  { feature: 'screen:/focus', label: 'Focus' },
  { feature: 'screen:/library', label: 'Library' },
  { feature: 'screen:/music', label: 'Music' },
  { feature: 'screen:/music/insights', label: 'Insights' },
  { feature: 'screen:/agents', label: 'Prime' },
  { feature: 'screen:/agents/$id', label: 'A Prime thread' },
  { feature: 'screen:/agents/all', label: 'Agents' },
  { feature: 'screen:/agents/graph', label: 'The graph' },
  { feature: 'screen:/agents/run/$id', label: 'An agent run' },
  { feature: 'screen:/agents/stats', label: 'Agent stats' },
  { feature: 'screen:/dev/releases', label: 'Releases' },
  { feature: 'screen:/infra', label: 'Infrastructure' },
  { feature: 'screen:/dev/review/$id', label: 'A pull request review' },
  { feature: 'screen:/activity', label: 'Activity' },
  { feature: 'screen:/whats-new', label: 'What’s new' },
  { feature: 'screen:/settings', label: 'Settings' },
  { feature: 'screen:/settings/$section', label: 'A Settings section' },
  { feature: 'action:capture', label: 'Capture a task' },
  { feature: 'action:prime-message', label: 'Message to Prime' },
  { feature: 'action:merge', label: 'Merge a pull request' },
  { feature: 'action:ship', label: 'Ship a release' },
  ...[
    'pin',
    'remind',
    'pause',
    'resume',
    'complete',
    'reopen',
    'reschedule',
    'people',
    'share',
    'share-with',
    'nudge-alfie',
    'nudge-members',
    'add-date',
    'edit',
    'delete',
  ].map((id) => ({
    feature: `action:actionbar:${id}`,
    label: `${ACTION_WORD[id] ?? id} button`,
  })),
]

/** The page's words for a feature, even one the catalogue has not heard of. */
export const usageLabel = (feature: string): string => {
  const known = USAGE_CATALOGUE.find((c) => c.feature === feature)
  if (known) return known.label
  const [kind, rest = ''] = [feature.slice(0, feature.indexOf(':')), feature.slice(feature.indexOf(':') + 1)]
  if (kind === 'action' && rest.startsWith('actionbar:')) {
    const id = rest.slice('actionbar:'.length)
    return `${ACTION_WORD[id] ?? id} button`
  }
  return rest
}

export type RankedUsage = {
  feature: string
  label: string
  kind: 'screen' | 'action'
  count: number
  before: number
  /** whole percent against the window before, null when there was nothing before */
  trend: number | null
  daily: number[]
  /** share of the busiest feature, 0..1, for the bar */
  share: number
  mostly: 'phone' | 'desktop' | null
}

const pick = (r: UsageRow, w: UsageWindow): [number, number] =>
  w === 7 ? [r.d7, r.p7] : w === 30 ? [r.d30, r.p30] : [r.d90, r.p90]

/** Features used in the window, most used first, each with its trend. */
export const rankUsage = (report: FeatureUsageReport, w: UsageWindow): RankedUsage[] => {
  const rows = report.features
    .map((r) => {
      const [count, before] = pick(r, w)
      return { r, count, before }
    })
    .filter((x) => x.count > 0)
    .sort((a, b) => b.count - a.count || a.r.feature.localeCompare(b.r.feature))
  const top = rows[0]?.count ?? 1
  return rows.map(({ r, count, before }) => ({
    feature: r.feature,
    label: usageLabel(r.feature),
    kind: r.feature.startsWith('action:') ? 'action' : 'screen',
    count,
    before,
    trend: before > 0 ? Math.round(((count - before) / before) * 100) : null,
    daily: r.daily.slice(-w),
    share: count / top,
    mostly:
      r.phone + r.desktop < 5
        ? null
        : r.desktop / (r.phone + r.desktop) > 0.8
          ? 'desktop'
          : r.phone / (r.phone + r.desktop) > 0.8
            ? 'phone'
            : null,
  }))
}

/** Days between two YYYY-MM-DD dates. */
const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86_400_000)

export type Unused = { feature: string; label: string; lastDay: string | null }

/**
 * Features with no use for 30 days: the catalogue's and any the record has
 * seen. Ones that were used once come first (newest drop-off first), then
 * those never used. Only once the record is 30 days old, so a fresh start flags nothing.
 */
export const unusedFor30 = (report: FeatureUsageReport): Unused[] => {
  if (!report.enabled || !report.since || daysBetween(report.since, report.today) < 30) return []
  const seen = new Map(report.features.map((r) => [r.feature, r]))
  const all = new Set([...USAGE_CATALOGUE.map((c) => c.feature), ...seen.keys()])
  return [...all]
    .filter((f) => (seen.get(f)?.d30 ?? 0) === 0)
    .map((feature) => ({ feature, label: usageLabel(feature), lastDay: seen.get(feature)?.last_day || null }))
    .sort((a, b) =>
      a.lastDay && b.lastDay
        ? b.lastDay.localeCompare(a.lastDay) || a.label.localeCompare(b.label)
        : a.lastDay
          ? -1
          : b.lastDay
            ? 1
            : a.label.localeCompare(b.label),
    )
}

/** Days of recording still to go before unused flags mean anything (0 when they do). */
export const daysToFlags = (report: FeatureUsageReport): number =>
  report.since ? Math.max(0, 30 - daysBetween(report.since, report.today)) : 30
